import React, { useContext, useEffect, useState } from 'react';
import { useLocalizedStrings } from '../../Localization';
import { AdminPanelPage } from '../AdminPanelPage';
import { Button, Card, CardHeader, Icon } from '@aidkitorg/component-library';
import { ConfigurationContext } from '../../Context';
import { ProgramContacts } from './components/ProgramContacts';
import { ProgramAssistant, ProgramAssistantSafe } from './components/ProgramAssistant';
import { ReportTechnicalIssueModal } from './components/ReportTechnicalIssueModal';
import { useProgramHome } from '../../Contexts/ProgramHomeContext';
import { WhatsNew } from './components/WhatsNew';
import InterfaceContext from '../../Context';
import { QuickActionLink } from '@aidkitorg/types/lib/survey';
import { useProgramHomeConfig } from '../shared/hooks/useAdminPanelPageConfig';

export const ProgramHomePage = () => {
  const L = useLocalizedStrings()
  const config = useContext(ConfigurationContext);
  const context = useContext(InterfaceContext);
  const [isReportIssueModalOpen, setIsReportIssueModalOpen] = useState(false);
  const { programHomeData, isProgramHomeLoading, fetchProgramHome } = useProgramHome();

  // Fetch page configuration using the hook
  const {
    pageConfig,
    isLoading: isConfigLoading,
    getLocalizedText
  } = useProgramHomeConfig();

  // Use AdminPanelConfig instead of ProgramConfig for these settings
  const showAdminPanelHome = !isConfigLoading && !!pageConfig;
  const enableAdminPanelProgramAssistant = !!pageConfig?.programAssistant?.workflowId;

  // Fetch program home data on mount
  useEffect(() => {
    if (showAdminPanelHome) {
      fetchProgramHome();
    }
  }, [showAdminPanelHome, fetchProgramHome]);

  // Extract configuration values
  const whatsNewTitle = getLocalizedText(['whatsNewToday', 'title'], L.admin_panel.program_home.whats_new.title);
  const welcomeMessage = getLocalizedText(['welcomeMessage'], L.admin_panel.program_home.welcome.replace('{name}', config.user.name));
  const programAssistantTitle = getLocalizedText(['programAssistant', 'title'], L.admin_panel.program_home.program_assistant.title);
  const programAssistantDescription = getLocalizedText(['programAssistant', 'description'], L.admin_panel.program_home.program_assistant.description);
  const quickActionsTitle = getLocalizedText(['quickActions', 'title'], L.admin_panel.program_home.quick_actions.title);
  const quickActionLinks = pageConfig?.quickActions?.links || [];
  const programContacts = pageConfig?.programContacts?.contacts || [];

  const quickActions = <Card className="p-1 w-full rounded-md">
    <CardHeader
      title={<div className='font-bold text-xl'>{quickActionsTitle}</div>}
    />
    <div>
      <div className="flex flex-col gap-y-4 pt-3">
        <Button
          variant="secondary"
          className="border !w-full !justify-start"
          onClick={() => setIsReportIssueModalOpen(true)}
        >
          <div className="flex items-center gap-x-3">
            <div>
              <Icon className='text-[var(--ak-identity-color-red)]' type="solid" name="BugAntIcon" role="decorative"/>
            </div>
            <div className="text-left">
              {L.admin_panel.program_home.quick_actions.report_technical_issue}
            </div>
          </div>
        </Button>
        {quickActionLinks.map((link: QuickActionLink, idx: number) => (
          <a
            key={idx}
            href={link.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center rounded text-sm font-bold py-[0.5rem] px-[0.75rem] transition-colors duration-300 !w-full !justify-start"
            style={{
              backgroundColor: 'var(--ak-color-button-secondary-background)',
              border: 'var(--ak-color-button-secondary-border)',
              color: 'var(--ak-color-button-secondary-text)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--ak-color-button-secondary-background-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--ak-color-button-secondary-background)'}
          >
            <div className="flex items-center gap-x-3">
              <div>
                <Icon className="text-[var(--ak-identity-color-blue)]" type="solid" name={link.icon} role="decorative"/>
              </div>
              <div className="text-left">
                {link.label[context.lang] || link.label['en']}
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
    <ProgramContacts contacts={programContacts} />
  </Card>

  return (
    <>
      <AdminPanelPage
        title={welcomeMessage}
        breadcrumbs={[
          {
            label: L.admin_panel.nav.home
          }
        ]}
      >
        {showAdminPanelHome ? (
          <div className="w-full">
            <div className="w-full pb-4">
              <WhatsNew
                programHomeData={programHomeData}
                isProgramHomeLoading={isProgramHomeLoading}
                title={whatsNewTitle}
              />
            </div>
            <div className="w-full">
              <div className="grid grid-cols-1 lg:grid-cols-7 gap-6">
                {/* Render the safe skeleton version of the program assistant when we haven't enabled the
                live version yet for a program. */}
                <div className="lg:col-span-4">
                  {enableAdminPanelProgramAssistant ? (
                    <ProgramAssistant
                      title={programAssistantTitle}
                      description={programAssistantDescription}
                    />
                  ) : (
                    <ProgramAssistantSafe
                      title={programAssistantTitle}
                      description={programAssistantDescription}
                    />
                  )}
                </div>
                <div className="lg:col-span-3">{quickActions}</div>
              </div>
            </div>
          </div>
        ) : <></>}

      </AdminPanelPage>

      <ReportTechnicalIssueModal
        open={isReportIssueModalOpen}
        onClose={() => setIsReportIssueModalOpen(false)}
      />
    </>
  )
}